/**
 * File: MonacoMarkdownEditor.tsx
 * Description: Monaco Editor component with integrated Mermaid preview
 * Author: GitHub Copilot
 * Created: 2025-08-26
 * Last Modified: 2025-08-26
 */

import { 
  PencilIcon, 
  DocumentTextIcon,
  Squares2X2Icon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import MonacoEditor, { type Monaco, type OnMount } from '@monaco-editor/react';
import React, { useState, useRef, useEffect } from 'react';

import { buildHeadingId } from '../../utils/headingAnchors';
import {
  closesFence,
  MERMAID_LANGUAGE,
  normalizeLineEndings,
  parseFenceLine,
  type FenceInfo,
} from '../../utils/markdownFences';
import { MermaidRenderer } from '../markdown/MermaidRenderer';

interface MonacoMarkdownEditorProps {
  value: string;
  onChange: (_value: string) => void;
  height?: string;
  /**
   * Grow the preview to fit its content instead of scrolling inside a fixed
   * height. Use when stacking many read-only sections on one page so the page
   * scrolls as a whole (avoids nested scroll boxes).
   */
  fitContent?: boolean;
  theme?: 'light' | 'dark';
  placeholder?: string;
  previewOnly?: boolean;
  iconOnlyButtons?: boolean;
  defaultViewMode?: ViewMode;
  isEditable?: boolean;
  isEditing?: boolean;
  onEditModeChange?: (_isEditing: boolean) => void;
  onSave?: () => void;
  isSaving?: boolean;
  initialFullscreen?: boolean;
  /** Architecture section key forwarded to MermaidRenderer for agent-specific fix guidelines */
  section?: string;
  /** Called with the full updated markdown when Archie fixes a Mermaid diagram.
   *  Defaults to calling onChange. Override in read-only contexts to persist directly. */
  onMermaidFix?: (_fixedValue: string) => void;
  /** One-line change descriptions for each Archie addition (in document
   *  order). Shown after the "Added by Archie" label on each green region. */
  archieChangeDescriptions?: string[];
  /** Real section headings (e.g. "## Security Architecture") that delimit
   *  document sections in a combined/full document. A green Archie addition
   *  ends where the next section begins, so an unbounded fix marker no longer
   *  turns every following section green. Leave empty for single-section
   *  previews (each editor already scopes its own additions). */
  sectionBoundaryTitles?: string[];
  /** When set, every rendered heading gets a stable DOM id of the form
   *  `${headingIdPrefix}-${slug}` so an external table-of-contents can scroll
   *  to individual sub-headings. Namespacing by section avoids id collisions
   *  when the same heading text appears in more than one section. */
  headingIdPrefix?: string;
}

type ViewMode = 'edit' | 'preview' | 'split';

/** Milliseconds after scrolling before the sync lock is released. */
const SYNC_LOCK_DELAY_MS = 50;

/** Base Tailwind classes shared by every toolbar button. */
const BTN_SM_BASE = 'btn btn-sm';

/** Active-state modifier class for all view-mode toolbar buttons. */
const BTN_SM_ACTIVE = 'btn-primary';

/** Inactive-state modifier class for all view-mode toolbar buttons. */
const BTN_SM_INACTIVE = 'btn-ghost';

/** Props for the standalone ViewModeToggle toolbar control. */
interface ViewModeToggleProps {
  previewOnly: boolean;
  isEditable: boolean;
  viewMode: ViewMode;
  onSetViewMode: (_mode: ViewMode) => void;
  isEditing: boolean;
  onSave?: () => void;
  isSaving: boolean;
  onEditModeChange?: (_isEditing: boolean) => void;
  isFullscreen: boolean;
  onSetFullscreen: (_full: boolean) => void;
}

const ViewModeToggle: React.FC<ViewModeToggleProps> = ({
  previewOnly,
  isEditable,
  viewMode,
  onSetViewMode,
  isEditing,
  onSave,
  isSaving,
  onEditModeChange,
  isFullscreen,
  onSetFullscreen,
}) => (
  <div className="flex gap-1 bg-base-200 rounded-lg p-1">
    {!previewOnly && !isEditable && (
      <>
        <button
          onClick={() => onSetViewMode('edit')}
          className={`${BTN_SM_BASE} ${viewMode === 'edit' ? BTN_SM_ACTIVE : BTN_SM_INACTIVE}`}
          title="Edit Mode (Ctrl+E)"
        >
          <PencilIcon className="w-4 h-4" />
        </button>
        <button
          onClick={() => onSetViewMode('split')}
          className={`${BTN_SM_BASE} ${viewMode === 'split' ? BTN_SM_ACTIVE : BTN_SM_INACTIVE}`}
          title="Split Mode"
        >
          <Squares2X2Icon className="w-4 h-4" />
        </button>
      </>
    )}
    <button
      onClick={() => onSetViewMode('preview')}
      className={`${BTN_SM_BASE} ${viewMode === 'preview' ? BTN_SM_ACTIVE : BTN_SM_INACTIVE}`}
      title="Preview Mode (Ctrl+P)"
      disabled={previewOnly}
    >
    </button>

    {/* Edit Controls */}
    {isEditable && (
      <>
        {isEditing ? (
          <>
            <button
              onClick={onSave}
              disabled={isSaving}
              className="btn btn-sm btn-success main-button"
              title="Save Changes"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => onEditModeChange?.(false)}
              disabled={isSaving}
              className="btn btn-sm btn-ghost main-button"
              title="Cancel Edit"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button
            onClick={() => onEditModeChange?.(true)}
            className="btn btn-sm btn-primary main-button"
            title="Edit Content"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
        )}
      </>
    )}

    <button
      onClick={() => onSetFullscreen(!isFullscreen)}
      className={`${BTN_SM_BASE} ${isFullscreen ? BTN_SM_ACTIVE : BTN_SM_INACTIVE}`}
      title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen (F11)'}
    >
      {isFullscreen ? (
        <ArrowsPointingInIcon className="w-4 h-4" />
      ) : (
        <ArrowsPointingOutIcon className="w-4 h-4" />
      )}
    </button>
  </div>
);

/** Initial view mode derived from the editor configuration. */
const getInitialViewMode = (
  previewOnly: boolean,
  isEditable: boolean,
  defaultViewMode?: ViewMode,
): ViewMode => {
  if (previewOnly) {
    return 'preview';
  }
  if (isEditable) {
    return 'preview'; // For editable content, start in preview mode
  }
  return defaultViewMode || 'edit';
};

const MARKDOWN_FENCE_PREFIX_NEWLINE_LEN = 12;
const MARKDOWN_FENCE_SUFFIX_NEWLINE_LEN = 4;
const MARKDOWN_FENCE_PREFIX_LEN = 11;
const MARKDOWN_FENCE_SUFFIX_LEN = 3;

/** Strip a whole-document ```markdown fence wrapper coming from the backend. */
const unwrapMarkdownFence = (content: string): string => {
  const trimmedContent = content.trim();
  if (trimmedContent.startsWith('```markdown\n') && trimmedContent.endsWith('\n```')) {
    // Extract the content between the markdown code block markers
    return trimmedContent.slice(MARKDOWN_FENCE_PREFIX_NEWLINE_LEN, -MARKDOWN_FENCE_SUFFIX_NEWLINE_LEN); // Remove ```markdown\n and \n```
  }
  if (trimmedContent.startsWith('```markdown') && trimmedContent.endsWith('```')) {
    // Handle case without newlines
    return trimmedContent.slice(MARKDOWN_FENCE_PREFIX_LEN, -MARKDOWN_FENCE_SUFFIX_LEN); // Remove ```markdown and ```
  }
  if (trimmedContent.startsWith('```markdown')) {
    // Opening wrapper the generator never closed: keep it and the whole
    // document would be parsed as one code block, hiding every diagram.
    const firstNewline = trimmedContent.indexOf('\n');
    return firstNewline === -1 ? '' : trimmedContent.slice(firstNewline + 1);
  }
  return content;
};

/**
 * A fenced code block opener/closer as permitted by CommonMark: up to three
 * leading spaces, three or more backticks (or tildes), optional info string.
 */

/** Width/border classes for the editor panel. */
const getEditorPanelClass = (
  isEditable: boolean,
  isEditing: boolean,
  viewMode: ViewMode,
): string => {
  if (isEditable) {
    return isEditing ? 'w-1/2 border-r border-base-300' : 'w-0 overflow-hidden';
  }
  return viewMode === 'split' ? 'w-1/2 border-r border-base-300' : 'w-full';
};

/** Width classes for the preview panel. */
const getPreviewPanelClass = (
  isEditable: boolean,
  isEditing: boolean,
  viewMode: ViewMode,
): string => {
  if (isEditable) {
    return isEditing ? 'w-1/2' : 'w-full';
  }
  return viewMode === 'split' ? 'w-1/2' : 'w-full';
};

export const MonacoMarkdownEditor: React.FC<MonacoMarkdownEditorProps> = ({
  value,
  onChange,
  height = '600px',
  fitContent = false,
  theme = 'light',
  placeholder = 'Start writing your markdown...',
  previewOnly = false,
  iconOnlyButtons = false,
  defaultViewMode,
  isEditable = false,
  isEditing = false,
  onEditModeChange,
  onSave,
  isSaving = false,
  initialFullscreen = false,
  section,
  onMermaidFix,
  archieChangeDescriptions,
  sectionBoundaryTitles,
  headingIdPrefix,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>(
    getInitialViewMode(previewOnly, isEditable, defaultViewMode),
  );
  const [isFullscreen, setIsFullscreen] = useState(initialFullscreen);
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const isSyncingScroll = useRef(false);
  const syncLockTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewModeRef = useRef<ViewMode>(viewMode);
  const isEditableRef = useRef(isEditable);
  const isEditingRef = useRef(isEditing);

  // Keep refs in sync with props/state so closures always read current values
  useEffect(() => { viewModeRef.current = viewMode; }, [viewMode]);
  useEffect(() => { isEditableRef.current = isEditable; }, [isEditable]);
  useEffect(() => { isEditingRef.current = isEditing; }, [isEditing]);

  /** True whenever both editor and preview panels are visible side-by-side */
  const isBothPanelsVisible = (): boolean => {
    if (isEditableRef.current) { return isEditingRef.current; }
    return viewModeRef.current === 'split';
  };

  const releaseSyncLock = () => {
    if (syncLockTimer.current !== null) { clearTimeout(syncLockTimer.current); }
    syncLockTimer.current = setTimeout(() => {
      isSyncingScroll.current = false;
      syncLockTimer.current = null;
    }, SYNC_LOCK_DELAY_MS);
  };

  const handleEditorDidMount = (editor: Parameters<OnMount>[0], monaco: Monaco) => {
    editorRef.current = editor;
    
    // Configure Monaco for Markdown
    monaco.languages.setLanguageConfiguration('markdown', {
      wordPattern: /-?\d+(?:\.\d+)?\w*|\w+/g,
    });

    // Add custom shortcuts only if not in preview-only mode
    if (!previewOnly) {
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyP, () => {
        setViewMode(prev => prev === 'preview' ? 'edit' : 'preview');
      });
    }

    // Sync editor scroll → preview scroll
    editor.onDidScrollChange(() => {
      if (isSyncingScroll.current) { return; }
      if (!isBothPanelsVisible()) { return; }
      const previewEl = previewRef.current;
      if (!previewEl) { return; }

      const editorScrollTop = editor.getScrollTop();
      const editorScrollHeight = editor.getScrollHeight();
      const editorClientHeight = editor.getLayoutInfo().height;
      const editorScrollable = editorScrollHeight - editorClientHeight;
      if (editorScrollable <= 0) { return; }

      const scrollPercent = editorScrollTop / editorScrollable;
      const previewScrollable = previewEl.scrollHeight - previewEl.clientHeight;

      isSyncingScroll.current = true;
      previewEl.scrollTop = scrollPercent * previewScrollable;
      releaseSyncLock();
    });
  };

  // Handle keyboard shortcuts for fullscreen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F11') {
        event.preventDefault();
        setIsFullscreen(!isFullscreen);
      }
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Update view mode when editing state changes
  useEffect(() => {
    if (isEditable) {
      // For editable content, we always stay in preview mode but show/hide the editor panel
      setViewMode('preview');
    } else if (isEditing && viewModeRef.current === 'preview') {
      setViewMode('edit');
    }
    // Don't automatically switch to preview mode if not using editable mode
    // Let the user control the view mode through the toggle buttons
  }, [isEditing, isEditable]);

  // Process inline markdown formatting like **bold**, *italic*, `code`, [links](url)
  const processInlineFormatting = (text: string): JSX.Element[] => {
    const elements: JSX.Element[] = [];
    let elementKey = 0;

    // Process links first (manual linear parser to avoid regex backtracking S5852)
    const linkMatches: Array<{ start: number; end: number; element: JSX.Element }> = [];
    const MAX_LABEL = 2000; // safety bounds to avoid extreme memory usage
    const MAX_URL = 4000;
    const textLength = text.length;
    for (let i = 0; i < textLength; i++) {
      if (text[i] !== '[') {continue;}
      // find closing bracket
      let j = i + 1;
      while (j < textLength && text[j] !== ']' && (j - i) <= MAX_LABEL) {j++;}
      if (j >= textLength || text[j] !== ']') {continue;} // no closing bracket
      if (text[j + 1] !== '(') { i = j; continue; }
      // find closing parenthesis
      let k = j + 2;
      while (k < textLength && text[k] !== ')' && (k - j) <= MAX_URL) {k++;}
      if (k >= textLength || text[k] !== ')') {continue;} // no closing paren
      const label = text.slice(i + 1, j);
      const url = text.slice(j + 2, k).trim();
      if (label.length === 0 || url.length === 0) { i = k; continue; }
      linkMatches.push({
        start: i,
        end: k + 1,
        element: (
          <a
            key={elementKey++}
            href={url}
            className="text-primary underline hover:text-primary-focus"
            target="_blank"
            rel="noopener noreferrer"
          >
            {label}
          </a>
        ),
      });
      i = k; // advance past the link
    }

    // Process other inline formats - using safe regex patterns to prevent ReDoS
    const otherPatterns = [
      { regex: /\*\*([^*\n]+)\*\*/g, component: (match: string) => <strong key={elementKey++}>{match}</strong> },
      { regex: /\*([^*\n]+)\*/g, component: (match: string) => <em key={elementKey++}>{match}</em> },
      { regex: /`([^`\n]+)`/g, component: (match: string) => <code key={elementKey++} className="bg-base-200 px-1 py-0.5 rounded text-sm">{match}</code> },
    ];

    const otherMatches: Array<{ start: number; end: number; element: JSX.Element }> = [];

    otherPatterns.forEach(pattern => {
      const regex = new RegExp(pattern.regex.source, 'g');
      let match;
      while ((match = regex.exec(text)) !== null) {
        otherMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          element: pattern.component(match[1]),
        });
      }
    });

    // Combine all matches
    const allMatches = [...linkMatches, ...otherMatches];

    // Sort matches by start position
    allMatches.sort((a, b) => a.start - b.start);

    // Remove overlapping matches (keep the first one)
    const validMatches = [];
    let lastEnd = 0;
    for (const match of allMatches) {
      if (match.start >= lastEnd) {
        validMatches.push(match);
        lastEnd = match.end;
      }
    }

    // Build the final elements array
    let textIndex = 0;
    validMatches.forEach(match => {
      // Add text before the match
      if (match.start > textIndex) {
        const beforeText = text.substring(textIndex, match.start);
        if (beforeText) {
          elements.push(<span key={elementKey++}>{beforeText}</span>);
        }
      }
      // Add the formatted element
      elements.push(match.element);
      textIndex = match.end;
    });

    // Add remaining text
    if (textIndex < text.length) {
      const remainingText = text.substring(textIndex);
      if (remainingText) {
        elements.push(<span key={elementKey++}>{remainingText}</span>);
      }
    }

    return elements.length > 0 ? elements : [<span key={0}>{text}</span>];
  };

  const renderMermaidBlock = (capturedBlock: string, key: number): JSX.Element => (
    <div
      key={key}
      className="my-4"
      // A big Mermaid diagram (e.g. a threat-model DFD) can render
      // thousands of px tall and dominate the scroll. In the
      // single-editor document views (PreviewScreen / section
      // editors) cap it so it scrolls within itself. In fit-to-content
      // mode the whole section is already capped (see the preview
      // panel), so we leave the diagram uncapped there to avoid a
      // nested scrollbar.
      style={
        fitContent
          ? undefined
          : { maxHeight: '70vh', overflow: 'auto' }
      }
    >
      <MermaidRenderer
        code={capturedBlock}
        section={section}
        onFixCode={(fixedCode) => {
          // capturedBlock ends with \n (each line appended with \n),
          // so trimEnd before building fences to avoid double-newline
          // mismatch against the stored markdown value.
          const oldBlock =
            `\`\`\`mermaid\n${capturedBlock.trimEnd()}\n\`\`\``;
          const newBlock =
            `\`\`\`mermaid\n${fixedCode.trimEnd()}\n\`\`\``;
          const newValue = value.includes(oldBlock)
            ? value.replace(oldBlock, newBlock)
            : value;
          // Use dedicated mermaid-fix handler when provided
          // (e.g. read-only preview that bypasses onChange no-op)
          if (onMermaidFix) {
            onMermaidFix(newValue);
          } else {
            onChange(newValue);
          }
        }}
      />
    </div>
  );

  const renderCodeBlock = (
    code: string,
    language: string,
    key: number,
  ): JSX.Element => {
    if (language === MERMAID_LANGUAGE) {
      return renderMermaidBlock(code, key);
    }
    return (
      <pre key={key} className="bg-base-200 p-4 rounded-lg my-4 overflow-x-auto">
        <code className={`language-${language}`}>{code}</code>
      </pre>
    );
  };

  // Stable anchor id for a heading, so an external table-of-contents can
  // scroll to it. Empty (unslugifiable) headings get no id.
  const headingAnchorId = (text: string): string | undefined =>
    buildHeadingId(headingIdPrefix, text) || undefined;

  const renderMarkdownLine = (line: string, key: number): JSX.Element => {
    if (line.startsWith('# ')) {
      return (
        <h1
          key={key}
          id={headingAnchorId(line.substring(2))}
          className="scroll-mt-6 text-3xl font-bold mt-6 mb-4 text-primary"
        >
          {processInlineFormatting(line.substring(2))}
        </h1>
      );
    }
    if (line.startsWith('## ')) {
      return (
        <h2
          key={key}
          id={headingAnchorId(line.substring(3))}
          className="scroll-mt-6 text-2xl font-semibold mt-5 mb-3 text-secondary"
        >
          {processInlineFormatting(line.substring(3))}
        </h2>
      );
    }
    if (line.startsWith('### ')) {
      return (
        <h3
          key={key}
          id={headingAnchorId(line.substring(4))}
          className="scroll-mt-6 text-xl font-medium mt-4 mb-2"
        >
          {processInlineFormatting(line.substring(4))}
        </h3>
      );
    }
    if (line.startsWith('#### ')) {
      return (
        <h4
          key={key}
          id={headingAnchorId(line.substring(5))}
          className="scroll-mt-6 text-lg font-medium mt-3 mb-2"
        >
          {processInlineFormatting(line.substring(5))}
        </h4>
      );
    }
    if (line.startsWith('##### ')) {
      return (
        <h5
          key={key}
          id={headingAnchorId(line.substring(6))}
          className="scroll-mt-6 text-base font-medium mt-3 mb-1"
        >
          {processInlineFormatting(line.substring(6))}
        </h5>
      );
    }
    if (line.startsWith('###### ')) {
      return (
        <h6
          key={key}
          id={headingAnchorId(line.substring(7))}
          className="scroll-mt-6 text-sm font-medium mt-2 mb-1"
        >
          {processInlineFormatting(line.substring(7))}
        </h6>
      );
    }
    if (line.startsWith('- ') || line.startsWith('* ')) {
      return (
        <li key={key} className="ml-4 my-1">
          {processInlineFormatting(line.substring(2))}
        </li>
      );
    }
    if (line.trim() === '---' || line.trim() === '***' || line.trim() === '___') {
      // Horizontal rule (separator)
      return <hr key={key} className="my-6 border-t border-base-300" />;
    }
    if (line.trim() === '') {
      return <br key={key} />;
    }
    if (line.includes('<span class="archie-fix-')) {
      return (
        <p
          key={key}
          className="my-2 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: line }}
        />
      );
    }
    return (
      <p key={key} className="my-2 leading-relaxed">
        {processInlineFormatting(line)}
      </p>
    );
  };

  const renderMarkdown = (content: string) => {
    // Handle content wrapped in markdown code blocks from backend.
    // Windows line endings are normalised first: a trailing \r would end up
    // inside the Mermaid source and break the parser.
    const processedContent = normalizeLineEndings(unwrapMarkdownFence(content));

    const lines = processedContent.split('\n');
    const elements: JSX.Element[] = [];
    // Unique React key per element. Keying by line index alone collides when a
    // single iteration emits two elements (e.g. flushing a table AND the line
    // that ended it), which made React duplicate/omit nodes and leave stale,
    // page-inflating DOM behind. A monotonic counter guarantees uniqueness.
    let keyCounter = 0;
    const nextKey = (): number => {
      const key = keyCounter;
      keyCounter += 1;
      return key;
    };
    let currentCodeBlock = '';
    let isInCodeBlock = false;
    let codeLanguage = '';
    let openFenceMarker = '';
    let currentTable: string[] = [];
    let isInTable = false;

    const renderTable = (tableLines: string[], key: number) => {
      if (tableLines.length < 2) {return null;}

      const headerLine = tableLines[0];
      const separatorLine = tableLines[1];
      const bodyLines = tableLines.slice(2);

      // Parse header
      const headers = headerLine.split('|').map(h => h.trim()).filter(h => h);
      
      // Check if it's a valid table (separator line should contain dashes)
      if (!separatorLine.includes('-')) {return null;}

      // Parse alignment from separator line
      const alignments = separatorLine.split('|').map(col => {
        const trimmed = col.trim();
        if (trimmed.startsWith(':') && trimmed.endsWith(':')) {return 'center';}
        if (trimmed.endsWith(':')) {return 'right';}
        return 'left';
      }).filter((_, index) => index < headers.length);

      // Parse body rows
      const rows = bodyLines
        .filter(line => line.includes('|'))
        .map(line => 
          line.split('|').map(cell => cell.trim()).filter(cell => cell),
        );

      return (
        <div
          key={key}
          className="my-4 overflow-x-auto rounded-lg border border-base-300 shadow-sm"
        >
          <table className="w-full border-collapse text-xs leading-snug">
            <thead>
              <tr className="bg-base-200/80">
                {headers.map((header, index) => (
                  <th
                    key={index}
                    className={`sticky top-0 z-10 whitespace-nowrap border-b border-base-300 bg-base-200/80 px-2.5 py-2 font-semibold text-base-content/80 text-${alignments[index] || 'left'}`}
                  >
                    {processInlineFormatting(header)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={
                    rowIndex % 2 === 0 ? 'bg-base-100' : 'bg-base-200/40'
                  }
                >
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className={`border-t border-base-300/70 px-2.5 py-1.5 align-top break-words text-base-content/90 text-${alignments[cellIndex] || 'left'}`}
                    >
                      {processInlineFormatting(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    };

    const flushTable = (): void => {
      if (!isInTable) { return; }
      // End current table and render it
      const tableElement = renderTable(currentTable, nextKey());
      if (tableElement) {elements.push(tableElement);}
      currentTable = [];
      isInTable = false;
    };

    const handleFenceLine = (fence: FenceInfo): void => {
      // End current table before starting code block
      flushTable();
      codeLanguage = fence.language;
      openFenceMarker = fence.marker;
      isInCodeBlock = true;
    };

    // Renders the buffered code block. Also called at the end of the document
    // so a fence the generator left unterminated still shows its diagram
    // instead of silently disappearing.
    const flushCodeBlock = (): void => {
      if (!isInCodeBlock) { return; }
      elements.push(renderCodeBlock(currentCodeBlock, codeLanguage, nextKey()));
      currentCodeBlock = '';
      isInCodeBlock = false;
      codeLanguage = '';
      openFenceMarker = '';
    };

    const appendTableLine = (line: string): void => {
      if (isInTable) {
        // Continue current table
        currentTable.push(line);
      } else {
        // Starting a new table
        isInTable = true;
        currentTable = [line];
      }
    };

    for (const line of lines) {
      const fence = parseFenceLine(line);
      if (isInCodeBlock) {
        if (fence && closesFence(fence, openFenceMarker)) {
          flushCodeBlock();
        } else {
          currentCodeBlock += `${line}\n`;
        }
      } else if (fence) {
        handleFenceLine(fence);
      } else if (line.includes('|') && line.trim() !== '') {
        // Line contains table syntax (has | characters)
        appendTableLine(line);
      } else {
        // Not a table line: regular markdown content
        flushTable();
        elements.push(renderMarkdownLine(line, nextKey()));
      }
    }

    // Handle any remaining code block / table at the end
    flushCodeBlock();
    flushTable();

    return elements;
  };

  const renderEditorPanel = (): JSX.Element => (
    <div className={getEditorPanelClass(isEditable, isEditing, viewMode)}>
      <MonacoEditor
        height="100%"
        defaultLanguage="markdown"
        value={value}
        onChange={(val) => onChange(val || '')}
        onMount={handleEditorDidMount}
        theme={theme === 'dark' ? 'vs-dark' : 'vs'}
        options={{
          wordWrap: 'on',
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          fontSize: 14,
          lineHeight: 1.6,
          fontFamily: 'JetBrains Mono, Consolas, "Courier New", monospace',
          automaticLayout: true,
          suggestOnTriggerCharacters: false,
          quickSuggestions: false,
          folding: true,
          lineNumbers: 'on',
          renderWhitespace: 'selection',
          bracketPairColorization: { enabled: true },
          padding: { top: 16, bottom: 16 },
          placeholder,
          readOnly: false, // Always allow editing when editor is visible
        }}
      />
    </div>
  );

  const renderPreviewContent = (): JSX.Element | JSX.Element[] => {
    // Archie fixes append each addition after a marker line
    // ("🛠️ Archie review update:"). Render the original content normally and
    // EACH addition in its own green "Added by Archie" block. Crucially, an
    // addition ENDS where the next real document section begins (see
    // sectionBoundaryTitles): the fix marker only marks the start, so in a
    // combined/full document an unbounded marker would otherwise turn every
    // following section green. Single-section previews pass no boundary titles
    // and keep the original behaviour (the addition runs to the section end).
    const marker = 'Archie review update';
    const valueLines = value.split('\n');
    if (!valueLines.some((line) => line.includes(marker))) {
      return renderMarkdown(value);
    }

    const boundaryTitles = (sectionBoundaryTitles ?? [])
      .map((title) => title.trim().toLowerCase())
      .filter(Boolean);
    const isSectionBoundary = (line: string): boolean => {
      if (boundaryTitles.length === 0) {
        return false;
      }
      const headingMatch = /^\s{0,3}#{1,3}\s+(.*)$/.exec(line);
      return headingMatch
        ? boundaryTitles.includes((headingMatch[1] ?? '').trim().toLowerCase())
        : false;
    };

    // Build ordered blocks so document order is preserved: normal prose, then
    // a green addition, then the next section back in normal prose, etc.
    type PreviewBlock = {
      type: 'normal' | 'added';
      lines: string[];
      key: string;
    };
    const blocks: PreviewBlock[] = [
      { type: 'normal', lines: [], key: 'preview-block-0' },
    ];
    let inAddition = false;
    // Track fenced code blocks. An Archie marker or a section-boundary heading
    // that happens to sit INSIDE a ```mermaid``` (or any) fence must NOT start a
    // new preview block: splitting a fence strands its closing ``` in a later
    // block, so renderMarkdown never flushes the diagram and it silently
    // vanishes — which reads as an endless "Rendering diagram..." spinner.
    let openFenceMarker = '';
    valueLines.forEach((line) => {
      const fence = parseFenceLine(line);
      if (fence && (!openFenceMarker || closesFence(fence, openFenceMarker))) {
        openFenceMarker = openFenceMarker ? '' : fence.marker;
        blocks.at(-1)?.lines.push(line);
        return;
      }
      const inFence = openFenceMarker !== '';
      if (!inFence && line.includes(marker)) {
        // marker line dropped
        blocks.push({
          type: 'added',
          lines: [],
          key: `preview-block-${blocks.length}`,
        });
        inAddition = true;
        return;
      }
      if (!inFence && inAddition && isSectionBoundary(line)) {
        blocks.push({
          type: 'normal',
          lines: [line],
          key: `preview-block-${blocks.length}`,
        });
        inAddition = false;
        return;
      }
      blocks.at(-1)?.lines.push(line);
    });

    let additionIndex = 0;
    return (
      <>
        {blocks.map((block) => {
          const text = block.lines.join('\n').trim();
          if (!text) {
            return null;
          }
          if (block.type === 'normal') {
            return (
              <div key={block.key}>
                {renderMarkdown(text)}
              </div>
            );
          }
          const description = archieChangeDescriptions?.[additionIndex];
          additionIndex += 1;
          return (
            <div
              key={block.key}
              className="archie-fix-region my-4 rounded-lg border-l-4 border-emerald-500 bg-emerald-50/60 py-3 pl-4 pr-3"
            >
              <div className="mb-2 text-xs font-semibold leading-snug text-emerald-700">
                ✨ Added by Archie
                {description ? `: ${description}` : ''}
              </div>
              <div>{renderMarkdown(text)}</div>
            </div>
          );
        })}
      </>
    );
  };

  const renderPreviewPanel = (): JSX.Element => (
    <div
      className={`${getPreviewPanelClass(isEditable, isEditing, viewMode)} min-h-0 bg-base-50`}
    >
      <div
        ref={previewRef}
        className={
          fitContent
            ? 'overflow-y-auto p-6'
            : 'h-full min-h-0 overflow-y-auto p-6'
        }
        // In fit-to-content mode the section has no fixed height, so a
        // very tall section (e.g. threat modeling with big Mermaid
        // diagrams) would stretch the page into an endless scroll. Cap
        // each section to the viewport and let it scroll internally -
        // the same bounded behaviour as the maximized editor. Short
        // sections stay their natural height (maxHeight never forces it).
        style={fitContent ? { maxHeight: '85vh' } : undefined}
        onScroll={(e) => {
          if (isSyncingScroll.current) { return; }
          if (!isBothPanelsVisible()) { return; }
          const editor = editorRef.current;
          if (!editor) { return; }

          const el = e.currentTarget;
          const previewScrollable = el.scrollHeight - el.clientHeight;
          if (previewScrollable <= 0) { return; }

          const scrollPercent = el.scrollTop / previewScrollable;
          const editorScrollHeight = editor.getScrollHeight();
          const editorClientHeight = editor.getLayoutInfo().height;
          const editorScrollable = editorScrollHeight - editorClientHeight;

          isSyncingScroll.current = true;
          editor.setScrollTop(scrollPercent * editorScrollable);
          releaseSyncLock();
        }}
      >
        {value ? (
          <div className="prose prose-lg max-w-none">
            {renderPreviewContent()}
          </div>
        ) : (
          <div
            className={`flex items-center justify-center text-base-content/60 ${
              fitContent ? 'py-12' : 'h-full'
            }`}
          >
            <div className="text-center">
              <DocumentTextIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Start writing to see preview</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const showEditorPanel = isEditable
    ? isEditing
    : viewMode === 'edit' || viewMode === 'split';
  const showPreviewPanel =
    isEditable || viewMode === 'preview' || viewMode === 'split';

  return (
    <div className={`border border-base-300 rounded-lg overflow-hidden ${
      isFullscreen 
        ? 'fixed inset-0 z-50 bg-white shadow-2xl' 
        : ''
    }`} style={isFullscreen ? { backgroundColor: '#ffffff' } : {}}>
      {/* Header with view mode toggle */}
      <div className="bg-base-100 border-b border-base-300 p-3 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <DocumentTextIcon className="w-5 h-5 text-primary" />
          <span className="font-medium">Markdown Editor</span>
        </div>
        <ViewModeToggle
          previewOnly={previewOnly}
          isEditable={isEditable}
          viewMode={viewMode}
          onSetViewMode={setViewMode}
          isEditing={isEditing}
          onSave={onSave}
          isSaving={isSaving}
          onEditModeChange={onEditModeChange}
          isFullscreen={isFullscreen}
          onSetFullscreen={setIsFullscreen}
        />
      </div>

      {/* Editor/Preview Content. In fit-to-content mode (stacked read-only
          document view) we drop the flex row + fixed height entirely and let
          the single preview panel size to its content, so the section never
          reserves extra height that shows up as an endless blank scroll. */}
      <div
        className={fitContent ? 'min-h-0' : 'flex min-h-0'}
        style={
          fitContent
            ? undefined
            : {
              height: isFullscreen ? 'calc(100vh - 120px)' : height,
            }
        }
      >
        {/* Editor Panel */}
        {showEditorPanel && renderEditorPanel()}

        {/* Preview Panel */}
        {showPreviewPanel && renderPreviewPanel()}
      </div>

      {/* Footer */}
      <div className="bg-base-100 border-t border-base-300">
        <span className="block px-3 py-2 text-xs text-base-content/60">Supports Mermaid diagrams</span>
      </div>
    </div>
  );
};
